import type { AlbumPage } from "../contracts/page";
import type { AlbumNode } from "../contracts/nodes";

function clone<T>(value: T): T {
    return typeof structuredClone === "function"
        ? structuredClone(value)
        : JSON.parse(JSON.stringify(value));
}

function getNodeId(node: any): string | null {
    const id = node?._id ?? node?.id;

    return id == null ? null : String(id);
}

function mergeDeep<T>(base: T, patch: Partial<T>): T {
    if ( !base || typeof base !== "object" || !patch || typeof patch !== "object" ) {
        return patch as T;
    }

    const result: any = clone(base);

    for (const key of Object.keys(patch as any)) {
        const patchValue = (patch as any)[key];

        if ( patchValue && typeof patchValue === "object" && !Array.isArray(patchValue) && result[key] && typeof result[key] === "object" && !Array.isArray(result[key])) {
            result[key] = mergeDeep( result[key], patchValue,);
        } else {
            result[key] = clone(patchValue);
        }
    }

    return result;
}

export function materializeNodeVersion(
    node: AlbumNode,
    version?: number | null,
): AlbumNode {
    const materialized = clone(node);

    if (version == null || !node.history?.length) {
        return materialized;
    }

    const history = [...node.history].sort(
        (a, b) => a.version - b.version,
    );

    const areEqual = (a: any, b: any) => {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            return false;
        }
    };

    for (let i = history.length - 1; i >= 0; i--) {
        const entry = history[i];

        if (entry.version <= version) {
            break;
        }

        const before = entry.diff?.before;
        const after = entry.diff?.after;

        if (!before) {
            continue;
        }

        if (before.transform) {
            materialized.transform = mergeDeep(
                materialized.transform,
                before.transform,
            );
        }

        if (before.style) {
            materialized.style = mergeDeep(
                materialized.style,
                before.style,
            );
        }

        if (before.constraints) {
            materialized.constraints = mergeDeep(
                materialized.constraints,
                before.constraints,
            );
        }

        if (before.metadata) {
            materialized.metadata = mergeDeep(
                materialized.metadata,
                before.metadata,
            );
        }

        const beforeContent = before.content;
        const afterContent = after?.content;

        const contentActuallyChanged =
            beforeContent !== undefined &&
            afterContent !== undefined &&
            !areEqual(beforeContent, afterContent);

        if (contentActuallyChanged) {
            materialized.content = mergeDeep(
                materialized.content,
                beforeContent,
            );
        }
    }

    materialized.history = clone(node.history);

    return materialized;
}



export function getPageNodeIdsForVersion(
    page: AlbumPage,
    version: number,
): string[] {
    const history =
        page.history ?? [];

    const membershipEntries =
        history
            .filter((entry: any) => Array.isArray( entry?.diff?.after?.nodes))
            .sort((a: any, b: any) => (a.version ?? 0) - (b.version ?? 0));


    if (!membershipEntries.length) {
        return (
            page.nodes ?? []
        )
            .map(getNodeId)
            .filter(
                (
                    id,
                ): id is string =>
                    id !== null,
            );
    }

    if (version <= 1) {
        const first =
            membershipEntries[0];

        const beforeNodes =
            first?.diff
                ?.before
                ?.nodes;

        if (Array.isArray(beforeNodes)) {
            return beforeNodes
                .map((id: any) =>
                    id == null
                        ? null
                        : String(id),
                )
                .filter(
                    (
                        id: string | null,
                    ): id is string =>
                        id !== null,
                );
        }

        return ( page.nodes ?? [] ).map(getNodeId).filter(
                ( id ): id is string => id !== null);
    }

    let selectedEntry: any = null;

    for ( const entry of membershipEntries ) {
        if ((entry.version ?? 0) <= version) {
            selectedEntry = entry;
        } else {
            break;
        }
    }

    if (!selectedEntry) {
        const first =  membershipEntries[0];

        const beforeNodes = first?.diff?.before?.nodes;

        if (Array.isArray(beforeNodes)) {
            return beforeNodes.map((id: any) => id == null ? null : String(id))
                   .filter(( id: string | null,): id is string => id !== null);
        }

        return [];
    }

    const ids =  selectedEntry?.diff?.after?.nodes;

    if (!Array.isArray(ids)) {
        return [];
    }

    return ids.map((id: any) => id == null ? null : String(id) )
        .filter(( id: string | null,): id is string => id !== null);
}

export function materializePageVersion(
    page: AlbumPage,
    version?: number | null,
): AlbumPage {
    if (version == null) {
        return page;
    }

    const storedNodes = page.nodes ?? [];

    const membershipIds = getPageNodeIdsForVersion( page, version );

    const nodeMap = new Map< string, AlbumNode >();

    for (  const node of storedNodes ) {
        const id = getNodeId(node);
        if (id) nodeMap.set( id, node );
    }

    const effectiveNodes:  AlbumNode[] = [];

    for ( const nodeId of membershipIds ) {
        const storedNode =  nodeMap.get(nodeId);

        if (!storedNode) {
            continue;
        }

        effectiveNodes.push(
            materializeNodeVersion(
                storedNode,
                version,
            ),
        );
    }

    return {
        ...clone(page),
        nodes: effectiveNodes,
    };
}
